/**
 * Duration-based rates, in slabs.
 *
 * <p>
 * The shape the trade actually quotes: "up to 3 days ₹6,500, up to 7 days ₹9,000, then ₹800 a day".
 * A rental is not priced per day — a wedding hire is a package with a standard period, and a shop
 * that charged 3 × the daily rate for the standard 3 days would be undercut by every neighbour.
 * </p>
 *
 * <p>
 * <strong>A flat price is one unbounded slab</strong>, which is why the model is slabs rather than
 * slabs-or-flat. A shop that charges ₹6,500 however long the booking runs configures a single slab
 * with no bound and no per-day rate, and never meets the concept again. Two pricing modes with a
 * switch between them would double every code path that touches a rate for the sake of a
 * configuration a slab already expresses.
 * </p>
 *
 * <p>
 * <strong>The server is authoritative and this is a preview.</strong> The figure a clerk sees while
 * picking dates has to update as they pick, which is not something a round trip per keystroke can
 * do — but nothing here is what gets charged. `RentalOrderItem.RentalRate` is frozen server-side at
 * the moment of booking, from the same slabs, because a wedding quoted in October and issued in
 * March must cost what it was quoted. If this function and the backend ever disagree, the backend
 * is right and this is the bug.
 * </p>
 */

/** One band of a rate card. */
export interface RateSlab {
  /**
   * Inclusive upper bound in days. `null` makes this the open-ended final slab, of which a rate
   * card has at most one — without it, a booking longer than the last bound has no price at all.
   */
  upToDays: number | null;
  /** The charge for a rental that lands in this band. */
  amount: number;
  /**
   * Charged for each day past the previous slab's bound, on the open-ended slab only.
   *
   * Absent or null means the open slab is flat — a price that does not grow however long the
   * booking runs, which is how a single-slab card expresses "one price, any duration".
   */
  perExtraDay?: number | null;
}

/** What a rental costs, and enough of the working to show a clerk why. */
export interface SlabCharge {
  /** Total for one garment over the whole period. */
  amount: number;
  /** The band that applied, for a label like "3-day rate". */
  slab: RateSlab;
  /** Days charged beyond the last bounded slab. Zero unless the open slab's per-day rate applied. */
  extraDays: number;
  /** What the garment's modifiers added, already in `amount`. Zero when there were none. */
  adjustmentPct: number;
}

/**
 * The card that prices one physical garment, as the server resolved it.
 *
 * <p>
 * <strong>Two numbers, and the client resolves neither of them.</strong> Which card applies runs
 * unit → variant → item → category → ancestors, and which modifiers survive depends on where that
 * search stopped — a card written against one serial code takes nothing on top, a card written
 * against a design takes both the size's adjustment and the garment's. Reimplementing that here is
 * how the counter and the invoice come to disagree, which this module's own history is a record of.
 * So the server sends the bands and the percentage that applies, and this multiplies.
 * </p>
 */
export interface UnitRateCard {
  bands: RateSlab[];
  /** Already reduced to what applies — zero when the card belongs to the garment itself. */
  adjustmentPct: number;
}

/**
 * Days between two `DateOnly`s, counting both ends.
 *
 * Inclusive because that is how the trade speaks and how the availability engine stores it — "12 to
 * 14 March" is three days — and the two must agree or a booking is charged for a different span
 * than the calendar blocks. Returns 0 on an unparseable or backwards range rather than a negative,
 * which would price a data-entry slip as a credit.
 */
export function chargeableDays(from: string, to: string): number {
  const start = Date.parse(`${from.slice(0, 10)}T00:00:00Z`);
  const end = Date.parse(`${to.slice(0, 10)}T00:00:00Z`);

  if (Number.isNaN(start) || Number.isNaN(end)) {
    return 0;
  }

  return Math.max(0, Math.round((end - start) / 86_400_000) + 1);
}

/**
 * Puts a rate card in the order it has to be read: bounded bands ascending, then the open one.
 *
 * A card is shop-entered data and arrives in whatever order somebody typed it. Reading it unsorted
 * would let a 7-day band shadow a 3-day one — the customer is charged the wrong slab and the total
 * still looks plausible, which is the worst kind of pricing bug.
 *
 * Exported because the rate-card screen has to *display* the ladder in the order the pricer reads
 * it. A second sort over there would be a second chance to get this wrong, on a screen whose whole
 * job is showing a shop what it charges.
 */
// Generic over the band rather than narrowing to `RateSlab`, so the master screen can sort its own
// rows — which carry an id, an owner and a scope as well — and get them back whole.
export function orderBands<T extends RateSlab>(slabs: readonly T[]): T[] {
  return [...slabs].sort((a, b) => {
    if (a.upToDays === null) {
      return 1;
    }

    if (b.upToDays === null) {
      return -1;
    }

    return a.upToDays - b.upToDays;
  });
}

/**
 * What one garment costs over `days`.
 *
 * <p>
 * Returns `null` when the card cannot price it — either no slabs at all, or a booking longer than
 * every bounded slab with no open slab to catch it. Null rather than 0, for the reason
 * {@link formatAmount} distinguishes them: an unpriced rate is unfinished setup and a zero is a
 * deliberate freebie, and a shop that sees "0" where it should see "no rate configured" will happily
 * hand out a sherwani for nothing.
 * </p>
 */
export function resolveSlabCharge(slabs: readonly RateSlab[], days: number): SlabCharge | null {
  // A booking always covers at least the day it starts on; a zero-day range is a data slip, not a
  // free rental, and pricing it at nothing would be the wrong way to report that.
  const billable = Math.max(1, Math.floor(days));

  const bands = orderBands(slabs);

  const bounded = bands.filter((slab) => slab.upToDays !== null);
  const open = bands.find((slab) => slab.upToDays === null);

  const fitting = bounded.find((slab) => billable <= (slab.upToDays ?? 0));

  if (fitting) {
    return { amount: fitting.amount, slab: fitting, extraDays: 0, adjustmentPct: 0 };
  }

  if (!open) {
    return null;
  }

  // Extra days run from the end of the last bounded band; with no bounded bands the open slab's
  // price already covers the first day, so only what follows it is extra.
  const coveredTo = bounded.at(-1)?.upToDays ?? 1;
  const extraDays = Math.max(0, billable - coveredTo);
  const perExtraDay = open.perExtraDay ?? 0;

  return {
    amount: open.amount + extraDays * perExtraDay,
    slab: open,
    // A flat open slab charges for no extra days however long it runs, and saying otherwise would
    // put "+4 days" on a quote beside a total that did not move.
    extraDays: perExtraDay > 0 ? extraDays : 0,
    adjustmentPct: 0,
  };
}

/**
 * What one garment costs — the ladder, times the modifier that applies to it.
 *
 * <p>
 * Mirrors `SlabPricing.Apply` on the server, including the two rules that are easy to lose: the
 * modifiers were <em>summed</em> before they arrived here, not compounded, and the result is floored
 * at zero because a combination below −100% would be the shop paying somebody to take a suit away.
 * </p>
 *
 * <p>
 * Null in stays null out: a garment nothing prices is not made priceable by a modifier. That
 * distinction — unfinished setup versus a deliberate freebie — is what stops a sherwani going out
 * for nothing, and the server refuses such a booking outright.
 * </p>
 */
export function priceGarment(
  card: UnitRateCard | null | undefined,
  days: number,
): SlabCharge | null {
  if (!card || card.bands.length === 0) {
    return null;
  }

  const charge = resolveSlabCharge(card.bands, days);

  if (charge === null || card.adjustmentPct === 0) {
    return charge;
  }

  const multiplier = 1 + card.adjustmentPct / 100;

  return {
    ...charge,
    amount: Math.round(Math.max(0, charge.amount * multiplier) * 100) / 100,
    adjustmentPct: card.adjustmentPct,
  };
}

/**
 * How the band reads on a quote — "3-day rate", "weekly rate", "extended".
 *
 * Derived rather than stored, because a label a shop typed would drift from the bound it names the
 * first time somebody edits one of the two.
 */
export function slabLabel(charge: SlabCharge): string {
  const { slab, extraDays, adjustmentPct } = charge;

  const band =
    slab.upToDays !== null
      ? `${slab.upToDays}-day rate`
      : extraDays > 0
        ? `Extended · +${extraDays} day${extraDays === 1 ? '' : 's'}`
        : 'Flat rate';

  // The modifier is named rather than folded silently into the figure: "₹7,475" against a card that
  // plainly says ₹6,500 is an argument at the counter. Matches `SlabPricing.Label` on the server,
  // which freezes the same words onto the garment at booking.
  if (adjustmentPct === 0) {
    return band;
  }

  return `${band} · ${adjustmentPct > 0 ? '+' : ''}${Number(adjustmentPct.toFixed(4))}%`;
}
